import type { CSSProperties, ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { isApiError, messageOf } from '@/api/errors';
import { Icon } from './Icon';
import { Button } from './Button';
import s from './States.module.css';

/** The empty state: what is missing and what to do about it. */
export function EmptyState({
  icon = 'inbox',
  title,
  children,
  action,
  compact,
}: {
  icon?: string;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div className={cx(s.state, compact && s.compact)}>
      <span className={s.iconWrap}>
        <Icon name={icon} size={compact ? 20 : 24} />
      </span>
      <p className={s.title}>{title}</p>
      {children && <p className={s.body}>{children}</p>}
      {action}
    </div>
  );
}

/** The error state, with retry. Entitlement errors render the locked component instead. */
export function ErrorState({
  error,
  onRetry,
  compact,
  title = 'This did not load',
}: {
  error: unknown;
  onRetry?: () => void;
  compact?: boolean;
  title?: string;
}) {
  const requestId = isApiError(error) ? error.requestId : undefined;
  return (
    <div className={cx(s.state, compact && s.compact)} role="alert">
      <span className={cx(s.iconWrap, s.neg)}>
        <Icon name="error" size={compact ? 20 : 24} />
      </span>
      <p className={s.title}>{title}</p>
      <p className={s.body}>{messageOf(error)}</p>
      {requestId && <p className={s.requestId}>Reference {requestId}</p>}
      {onRetry && (
        <Button size="sm" icon="refresh" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

/** A shimmering placeholder block. */
export function Skeleton({ width = '100%', height = 14, radius = 6, style }: { width?: number | string; height?: number | string; radius?: number; style?: CSSProperties }) {
  return <span className={s.skeleton} style={{ width, height, borderRadius: radius, ...style }} aria-hidden />;
}

/** Skeleton rows for a table or list. */
export function SkeletonRows({ rows = 6, height = 40 }: { rows?: number; height?: number }) {
  return (
    <div className={s.rows} aria-busy aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className={s.row} style={{ height }}>
          <Skeleton width="18%" />
          <Skeleton width="28%" />
          <Skeleton width="12%" />
          <Skeleton width="22%" />
        </div>
      ))}
    </div>
  );
}

/** Skeleton for a chart area. */
export function SkeletonChart({ height = 250 }: { height?: number }) {
  return (
    <div className={s.chart} style={{ height }} aria-busy aria-label="Loading chart">
      <Skeleton height="100%" radius={8} />
    </div>
  );
}

export function Spinner({ size = 18 }: { size?: number }) {
  return <span className={s.spinner} style={{ width: size, height: size }} role="status" aria-label="Loading" />;
}

/** Full-area loading for route-level fetches. */
export function LoadingArea({ label = 'Loading' }: { label?: string }) {
  return (
    <div className={s.loadingArea}>
      <Spinner size={22} />
      <span>{label}</span>
    </div>
  );
}
